import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, ChevronLeft, ChevronRight, Phone, Mail, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_SLIDES = [
  {
    img: 'https://customer-assets.emergentagent.com/job_realestate-cms-15/artifacts/gapu7kq2_Dream%20Riveria%20Residential%20cam%2001.jpg.jpeg',
    badge: 'ONGOING · PUNE',
    title: "Dreams Riviera",
    sub: "Lonikalbhor, Pune–Solapur Highway",
    id: 'res-001',
  },
  {
    img: 'https://customer-assets.emergentagent.com/job_realestate-cms-15/artifacts/g3o9ptqj_Dream%20Kokan%20City_1.jpg',
    badge: 'ONGOING · RAIGAD',
    title: "Dream Kokan City",
    sub: "Taluka Mhasla, District Raigad, Maharashtra",
    id: 'plot-001',
  },
  {
    img: 'https://customer-assets.emergentagent.com/job_realestate-cms-15/artifacts/cz9ul6n4_Dream%20Nagpur%20City_4.png',
    badge: 'ONGOING · NAGPUR',
    title: "Dream Nagpur City",
    sub: "Ghubadi, Taluka Katol, District Nagpur",
    id: 'plot-002',
  },
  {
    img: 'https://customer-assets.emergentagent.com/job_realestate-cms-15/artifacts/bfmo7acn_Sai%20plaza_1.png',
    badge: 'ONGOING · PUNE',
    title: "Sai Plaza",
    sub: "Rajgurunagar, Pune, Maharashtra",
    id: 'comm-001',
  },
  {
    img: 'https://customer-assets.emergentagent.com/job_realestate-cms-15/artifacts/thifgmxx_Krishna%20Heights_1.png',
    badge: 'COMPLETED · PUNE',
    title: "Siddhivinayak Heights",
    sub: "Kharadi, Pune, Maharashtra",
    id: 'res-006',
  },
];

const STATS = [
  { value: '15+', label: 'Years of Excellence' },
  { value: '11+', label: 'Premium Projects' },
  { value: '3000+', label: 'Happy Families' },
  { value: '5+', label: 'Cities Across Maharashtra' },
];

const CATEGORIES = [
  { icon: '🏢', label: 'Residential', desc: 'Premium apartments & heights', path: '/projects?category=residential' },
  { icon: '🌿', label: 'Plotting', desc: 'Strategic land investments', path: '/projects?category=plotting' },
  { icon: '🏪', label: 'Commercial', desc: 'Office & retail spaces', path: '/projects?category=commercial' },
];

const STATUS_STYLE = {
  Ongoing:   { bg: '#1a3a2a', text: '#4ade80', label: 'ONGOING' },
  Completed: { bg: '#1a2a3a', text: '#60a5fa', label: 'COMPLETED' },
  Upcoming:  { bg: '#3a2a1a', text: '#fbbf24', label: 'UPCOMING' },
};

export default function Home() {
  const [projects, setProjects] = useState([]);
  const [slide, setSlide] = useState(0);
  const [form, setForm] = useState({ name: '', phone: '', email: '', project: '' });
  const [submitted, setSubmitted] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/projects`).then(r => setProjects(r.data || [])).catch(() => {});
  }, []);

  const startTimer = () => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setSlide(s => (s + 1) % HERO_SLIDES.length), 5000);
  };
  useEffect(() => { startTimer(); return () => clearInterval(timerRef.current); }, []);

  const goSlide = (idx) => { setSlide(idx); startTimer(); };
  const prevSlide = () => goSlide((slide - 1 + HERO_SLIDES.length) % HERO_SLIDES.length);
  const nextSlide = () => goSlide((slide + 1) % HERO_SLIDES.length);

  const handleEnquiry = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  const featured = projects.slice(0, 6);
  const ongoing = projects.filter(p => p.status === 'Ongoing').slice(0, 4);
  const completed = projects.filter(p => p.status === 'Completed').slice(0, 4);

  return (
    <div className="pt-16 font-sans">

      {/* ── HERO SLIDESHOW ── */}
      <section className="relative h-[92vh] min-h-[540px] overflow-hidden bg-[#0D1E2C]">
        {HERO_SLIDES.map((s, i) => (
          <div
            key={i}
            className="absolute inset-0 transition-opacity duration-1000"
            style={{ opacity: i === slide ? 1 : 0, zIndex: i === slide ? 1 : 0 }}
          >
            <img src={s.img} alt={s.title} className="w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, rgba(13,30,44,0.55) 40%, rgba(13,30,44,0.1) 100%)' }} />
          </div>
        ))}

        {/* Content */}
        <div className="relative z-10 h-full flex flex-col justify-center px-8 md:px-20 max-w-3xl">
          <span className="inline-block text-[#C9A84C] text-xs font-bold tracking-[0.3em] mb-4 border border-[#C9A84C]/40 px-3 py-1 rounded-sm w-fit">
            {HERO_SLIDES[slide].badge}
          </span>
          <h1 className="text-4xl md:text-6xl font-bold text-white leading-tight mb-3" style={{ fontFamily: 'Georgia, serif' }}>
            {HERO_SLIDES[slide].title}
          </h1>
          <p className="text-stone-300 text-base md:text-lg mb-8 flex items-center gap-2">
            <MapPin className="w-4 h-4 text-[#C9A84C]" /> {HERO_SLIDES[slide].sub}
          </p>
          <Link
            to={`/projects/${HERO_SLIDES[slide].id}`}
            className="inline-flex items-center gap-2 bg-[#C9A84C] hover:bg-[#b8962f] text-[#0D1E2C] font-bold text-sm px-7 py-3 rounded transition-all w-fit"
          >
            EXPLORE PROJECT <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Arrows */}
        <button onClick={prevSlide} className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-black/30 hover:bg-[#C9A84C] text-white flex items-center justify-center transition-all">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button onClick={nextSlide} className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-black/30 hover:bg-[#C9A84C] text-white flex items-center justify-center transition-all">
          <ChevronRight className="w-5 h-5" />
        </button>

        {/* Dots */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex gap-2">
          {HERO_SLIDES.map((_, i) => (
            <button key={i} onClick={() => goSlide(i)} className={`w-2 h-2 rounded-full transition-all ${i === slide ? 'bg-[#C9A84C] w-6' : 'bg-white/40'}`} />
          ))}
        </div>
      </section>

      {/* ── STATS BAR ── */}
      <section className="bg-[#0D1E2C] py-8 border-t border-[#C9A84C]/20">
        <div className="max-w-5xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {STATS.map((s, i) => (
            <div key={i} className={`${i < 3 ? 'md:border-r border-white/10' : ''}`}>
              <p className="text-3xl font-bold text-[#C9A84C]" style={{ fontFamily: 'Georgia, serif' }}>{s.value}</p>
              <p className="text-stone-400 text-xs tracking-wide mt-1 uppercase">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── ABOUT + CATEGORIES ── */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-14 items-center">
          <div>
            <p className="text-[#C9A84C] text-xs font-bold tracking-[0.3em] mb-3 uppercase">About Us</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0D1E2C] leading-snug mb-5" style={{ fontFamily: 'Georgia, serif' }}>
              Creating Landmark<br />Communities Across<br />Maharashtra
            </h2>
            <p className="text-stone-500 leading-relaxed mb-6">
              DBK Group Of Companies has been transforming land into landmark communities since 2009. From premium residential apartments in Pune to vast plotting schemes across Maharashtra, our commitment is to quality, trust, and excellence in every square foot we develop.
            </p>
            <div className="flex flex-col gap-2 mb-7">
              {['RERA registered & compliant projects','On-time delivery track record','Transparent pricing & documentation','Post-sales support & community management'].map(f => (
                <div key={f} className="flex items-center gap-2 text-stone-600 text-sm">
                  <CheckCircle className="w-4 h-4 text-[#C9A84C] shrink-0" /> {f}
                </div>
              ))}
            </div>
            <Link to="/about" className="inline-flex items-center gap-2 text-[#0D1E2C] font-semibold text-sm border-b-2 border-[#C9A84C] pb-0.5 hover:text-[#C9A84C] transition-colors">
              Read More About Us <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-3 gap-4">
            {CATEGORIES.map(c => (
              <Link key={c.label} to={c.path} className="group flex flex-col items-center text-center p-5 rounded-xl border border-stone-100 hover:border-[#C9A84C] hover:shadow-lg transition-all">
                <span className="text-3xl mb-3">{c.icon}</span>
                <p className="font-semibold text-[#0D1E2C] text-sm mb-1">{c.label}</p>
                <p className="text-stone-400 text-xs leading-snug">{c.desc}</p>
              </Link>
            ))}
            <div className="col-span-3 rounded-xl overflow-hidden h-40">
              <img src="https://customer-assets.emergentagent.com/job_realestate-cms-15/artifacts/xxklsyt9_Dreams%20Riviera%20B%20Wing%201.png" alt="DBK Projects" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* ── ONGOING PROJECTS ── */}
      <section className="py-20 bg-[#F7F6F2]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-end justify-between mb-10">
            <div>
              <p className="text-[#C9A84C] text-xs font-bold tracking-[0.3em] mb-2 uppercase">Our Portfolio</p>
              <h2 className="text-3xl font-bold text-[#0D1E2C]" style={{ fontFamily: 'Georgia, serif' }}>Ongoing Projects</h2>
            </div>
            <Link to="/projects" className="text-sm font-semibold text-[#0D1E2C] border-b border-[#C9A84C] hover:text-[#C9A84C] hidden md:flex items-center gap-1 transition-colors">
              View All <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {ongoing.map(p => <ProjectCard key={p.id} project={p} />)}
          </div>
        </div>
      </section>

      {/* ── COMPLETED PROJECTS ── */}
      {completed.length > 0 && (
        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-6">
            <div className="flex items-end justify-between mb-10">
              <div>
                <p className="text-[#C9A84C] text-xs font-bold tracking-[0.3em] mb-2 uppercase">Delivered</p>
                <h2 className="text-3xl font-bold text-[#0D1E2C]" style={{ fontFamily: 'Georgia, serif' }}>Completed Projects</h2>
              </div>
              <Link to="/projects" className="text-sm font-semibold text-[#0D1E2C] border-b border-[#C9A84C] hover:text-[#C9A84C] hidden md:flex items-center gap-1 transition-colors">
                View All <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {completed.map(p => <ProjectCard key={p.id} project={p} />)}
            </div>
          </div>
        </section>
      )}

      {/* ── ENQUIRY SECTION ── */}
      <section className="py-20 bg-[#0D1E2C] relative overflow-hidden">
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, #C9A84C 0%, transparent 60%)' }} />
        <div className="max-w-5xl mx-auto px-6 relative z-10 grid md:grid-cols-2 gap-14 items-center">
          <div>
            <p className="text-[#C9A84C] text-xs font-bold tracking-[0.3em] mb-3 uppercase">Get In Touch</p>
            <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Georgia, serif' }}>
              Submit Your Details<br />For More Information
            </h2>
            <p className="text-stone-400 mb-8 text-sm leading-relaxed">
              Our sales team will get back to you within 24 hours with detailed project information, pricing, and site visit arrangements.
            </p>
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-stone-300 text-sm">
                <Phone className="w-4 h-4 text-[#C9A84C]" /> +91 8625009659
              </div>
              <div className="flex items-center gap-3 text-stone-300 text-sm">
                <Mail className="w-4 h-4 text-[#C9A84C]" /> info@siddhivinayakinfra.estate
              </div>
              <div className="flex items-center gap-3 text-stone-300 text-sm">
                <MapPin className="w-4 h-4 text-[#C9A84C]" /> Office No. BR2-330A to 334, Jai Ganesh Vision, Akurdi, Pune
              </div>
            </div>
          </div>
          <div>
            {submitted ? (
              <div className="bg-white/5 border border-[#C9A84C]/30 rounded-xl p-8 text-center">
                <CheckCircle className="w-12 h-12 text-[#C9A84C] mx-auto mb-4" />
                <h3 className="text-white font-bold text-lg mb-2">Thank You!</h3>
                <p className="text-stone-400 text-sm">We'll contact you within 24 hours.</p>
              </div>
            ) : (
              <form onSubmit={handleEnquiry} className="bg-white/5 border border-white/10 rounded-xl p-6 space-y-4">
                <div>
                  <label className="text-stone-400 text-xs uppercase tracking-wide mb-1 block">Full Name *</label>
                  <input required value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))}
                    placeholder="Your name"
                    className="w-full bg-white/5 border border-white/10 text-white placeholder-stone-500 px-4 py-2.5 rounded text-sm outline-none focus:border-[#C9A84C] transition-colors" />
                </div>
                <div>
                  <label className="text-stone-400 text-xs uppercase tracking-wide mb-1 block">Phone Number *</label>
                  <input required value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))}
                    placeholder="+91 XXXXX XXXXX"
                    className="w-full bg-white/5 border border-white/10 text-white placeholder-stone-500 px-4 py-2.5 rounded text-sm outline-none focus:border-[#C9A84C] transition-colors" />
                </div>
                <div>
                  <label className="text-stone-400 text-xs uppercase tracking-wide mb-1 block">Email</label>
                  <input value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))}
                    placeholder="your@email.com"
                    className="w-full bg-white/5 border border-white/10 text-white placeholder-stone-500 px-4 py-2.5 rounded text-sm outline-none focus:border-[#C9A84C] transition-colors" />
                </div>
                <div>
                  <label className="text-stone-400 text-xs uppercase tracking-wide mb-1 block">Project of Interest</label>
                  <select value={form.project} onChange={e => setForm(f => ({...f, project: e.target.value}))}
                    className="w-full bg-[#0D1E2C] border border-white/10 text-white px-4 py-2.5 rounded text-sm outline-none focus:border-[#C9A84C] transition-colors">
                    <option value="">Select a project</option>
                    <option>Dreams Riviera 'A' Wing</option>
                    <option>Dreams Riviera 'B' Wing</option>
                    <option>Dream Kokan City</option>
                    <option>Dream Nagpur City</option>
                    <option>Dream Latur City</option>
                    <option>Sai Plaza</option>
                    <option>Jay Ganesh Complex</option>
                  </select>
                </div>
                <button type="submit" className="w-full bg-[#C9A84C] hover:bg-[#b8962f] text-[#0D1E2C] font-bold py-3 rounded text-sm transition-colors">
                  SUBMIT ENQUIRY
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function ProjectCard({ project }) {
  const st = STATUS_STYLE[project.status] || STATUS_STYLE.Ongoing;
  return (
    <Link to={`/projects/${project.id}`} className="group block bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
      <div className="relative overflow-hidden h-48">
        <img
          src={project.image_url || project.imageUrl || 'https://images.pexels.com/photos/30608874/pexels-photo-30608874.jpeg?auto=compress&cs=tinysrgb&h=400'}
          alt={project.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
        <span className="absolute top-3 left-3 text-[10px] font-bold px-2 py-1 rounded" style={{ background: st.bg, color: st.text }}>
          {st.label}
        </span>
      </div>
      <div className="p-4">
        <h3 className="font-bold text-[#0D1E2C] text-sm mb-1 group-hover:text-[#C9A84C] transition-colors leading-snug">{project.title}</h3>
        <p className="text-stone-400 text-xs flex items-start gap-1">
          <MapPin className="w-3 h-3 shrink-0 mt-0.5 text-[#C9A84C]" />
          <span className="line-clamp-2">{project.location}</span>
        </p>
      </div>
    </Link>
  );
}
